import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import { storageDocuments } from "@/firebase/firebaseConfig";
import { TASK_MANAGER_CATEGORY, TASK_MANAGER_STATUS } from "@/config/const";
import { HtmlUtil } from "@/utils";
import moment from "moment-timezone";

const taskManagerApi = AiseaApiFactory.get("enterpriseBackgroundTask");

export default {
  async fetchTaskCount(_, payload) {
    const res = await taskManagerApi.count(payload);
    return res.data.count;
  },

  async fetchTotalTaskCount({ dispatch, commit, state, getters }) {
    try {
      const payload = {
        status: state.selectedFilterStatus,
        type: state.selectedFilterCategory,
      };

      const count = await dispatch("fetchTaskCount", payload);

      const allTaskcount = count + getters.filteredLocalTasksCount;
      commit("SET_TOTAL_TASK_COUNT", allTaskcount);
    } catch (error) {
      console.error(error);
    }
  },

  async fetcAllProgressTaskCount({ dispatch, commit, getters }) {
    const payload = {
      status: TASK_MANAGER_STATUS.PROGRESS,
    };

    const count =
      (await dispatch("fetchTaskCount", payload)) +
      getters.localProgressTaskCount;
    commit("SET_TOTAL_PROGRESS_TASK_COUNT", count);
  },

  async fetchUploadProgressTaskCount({ dispatch, commit, getters }) {
    const payload = {
      status: TASK_MANAGER_STATUS.PROGRESS,
      type: TASK_MANAGER_CATEGORY.UPLOAD,
    };

    const count =
      (await dispatch("fetchTaskCount", payload)) +
      getters.localProgressTaskCount;
    commit("SET_UPLOAD_PROGRESS_TASK_COUNT", count);
  },

  async fetchDownloadProgressTaskCount({ dispatch, commit }) {
    const payload = {
      status: TASK_MANAGER_STATUS.PROGRESS,
      type: TASK_MANAGER_CATEGORY.DOWNLOAD,
    };

    const count = await dispatch("fetchTaskCount", payload);
    commit("SET_DOWNLOAD_PROGRESS_TASK_COUNT", count);
  },

  async fetchProgressTaskCount({ dispatch }) {
    const promiseAll = [
      dispatch("fetcAllProgressTaskCount"),
      dispatch("fetchUploadProgressTaskCount"),
      dispatch("fetchDownloadProgressTaskCount"),
    ];

    await Promise.all(promiseAll);
  },

  //タスク一覧取得
  async fetchTasks({ state, getters }) {
    if (getters.requestTaskSize <= 0) {
      return [];
    }

    const param = {
      name: state.filterName,
      status: state.selectedFilterStatus,
      type: state.selectedFilterCategory,
      size: getters.requestTaskSize,
      offset: getters.requestTaskOffset,
      page: getters.requestPage,
    };

    const res = await taskManagerApi.fetch(param);
    const tasks = res.data.tasks.map(task => {
      if (task.properties) {
        const escape = HtmlUtil.htmlEscape(task.properties);
        const props = JSON.parse(escape);
        task.bytes = parseInt(props.bytes, 10);
        task.contentType = props.contentType;
        task.vesselId = props.vesselId;
      }

      task.created_at = moment(task.created_at).format(
        "YYYY-MM-DD HH:mm:ss.SSS"
      );
      task.updated_at = moment(task.updated_at).format(
        "YYYY-MM-DD HH:mm:ss.SSS"
      );

      return task;
    });

    return tasks;
  },

  async mergeAllTaskList({ dispatch, getters, commit }) {
    const sortFunc = (a, b) => {
      if (a.updated_at > b.updated_at) return -1;
      if (a.updated_at < b.updated_at) return 1;
      return 0;
    };

    const tasks = await dispatch("fetchTasks");

    const displayLocalTasks = getters.displayLocalTask;

    //ローカルのタスク一覧とAPIで取得したタスク一覧をマージ
    const allTasks = tasks.concat(displayLocalTasks).sort(sortFunc);

    //進行中のタスクでフィルタ後、更新順でソート
    const progressTasks = allTasks
      .filter(item => item.status === TASK_MANAGER_STATUS.PROGRESS)
      .sort(sortFunc);

    //完了のタスクでフィルタ後、更新順でソート
    const completedTasks = allTasks
      .filter(item => item.status === TASK_MANAGER_STATUS.COMPLETED)
      .sort(sortFunc);

    const allSortedTask = progressTasks.concat(completedTasks);
    commit("SET_TASKS", allSortedTask);
  },

  async fetchAllTaskList({ dispatch }) {
    try {
      await dispatch("fetchTotalTaskCount");
      await dispatch("fetchProgressTaskCount");
      await dispatch("mergeAllTaskList");
    } catch (error) {
      console.error(error);
    }
  },

  //タスク検索
  async searchTask() {},

  async upload({ commit, dispatch }, payload) {
    const task = payload.task;

    const metadata = {
      customMetadata: {
        important: false,
      },
    };

    const ref = storageDocuments.child(task.url);
    ref.put(payload.file, metadata).on(
      "state_changed",
      snapshot => {
        const total = snapshot.totalBytes;
        const transferred = snapshot.bytesTransferred;
        const progress = (transferred / total) * 100;
        const progressInt = parseInt(progress, 10);

        task.transferred = transferred;

        const _payload = {
          task: task,
          status: {
            progress: progressInt,
          },
        };
        commit("UPDATE_LOCAL_TASK", _payload);
      },
      error => {
        const _payload = {
          task: task,
          status: {
            status: TASK_MANAGER_STATUS.ERROR,
          },
        };
        commit("UPDATE_LOCAL_TASK", _payload);
        throw error;
      },
      async () => {
        commit("DELETE_LOCAL_TASK", task);
        const _payload = {
          name: task.name,
          props: task.props,
          url: task.url,
        };
        payload.completedFunc(task);
        await dispatch("addUploadTask", _payload);
      }
    );
  },

  // アップロードタスクのローカル登録
  async startUploadTask({ commit, dispatch }, payload) {
    try {
      const task = {
        name: payload.name,
        progress: 0,
        status: TASK_MANAGER_STATUS.PROGRESS, //進行中固定
        type: TASK_MANAGER_CATEGORY.UPLOAD,
        created_at: moment().format("YYYY-MM-DD HH:mm:ss.SSS"),
        updated_at: moment().format("YYYY-MM-DD HH:mm:ss.SSS"),
        url: payload.path,
        props: payload.props,
        bytes: payload.props.bytes,
      };
      dispatch("upload", {
        task: task,
        file: payload.file,
        completedFunc: payload.completedFunc,
      });

      commit("ADD_LOCAL_TASK", task);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  //ダウンロードタスク登録
  async addDownloadTask({ dispatch }, task) {
    const payload = {
      name: task.name,
      progress: 0,
      properties: JSON.stringify(task.props),
      status: TASK_MANAGER_STATUS.PROGRESS,
      type: TASK_MANAGER_CATEGORY.DOWNLOAD,
      url: task.path,
      bytes: task.props.bytes,
    };
    const res = await taskManagerApi.add(payload);
    await dispatch("fetchAllTaskList");
    return res.data;
  },

  //アップロード完了タスク登録
  async addUploadTask({ dispatch }, task) {
    const payload = {
      name: task.name,
      progress: 0,
      properties: JSON.stringify(task.props),
      status: TASK_MANAGER_STATUS.COMPLETED, //アップロードは完了後のタスク追加のため、完了固定
      type: TASK_MANAGER_CATEGORY.UPLOAD,
      url: task.url,
    };
    await taskManagerApi.add(payload);
    await dispatch("fetchAllTaskList");
  },

  //タスク更新
  async updateTask({ dispatch }, payload) {
    await taskManagerApi.update(payload.id, payload.body);
    await dispatch("fetchAllTaskList");
  },

  //タスク削除
  async removeTask({ dispatch }, id) {
    await taskManagerApi.delete(id);
    await dispatch("fetchAllTaskList");
  },
};
